import { BuildCacheToolIds } from '@/models/BuildCacheTool';
import { BuildToolInvocationStatus, BuildToolInvocationWithDetails } from '@/models/BuildToolInvocation';

// React Native reports cache activity on the child invocations only, so the parent invocation the list
// shows always derives to "No activity" — which reads as "your cache did nothing" rather than "not
// measured here". Hide the column until we can aggregate the children into a meaningful parent status.
export const hidesCacheStatusColumn = (tool: BuildCacheToolIds) => tool === BuildCacheToolIds.reactNative;

export enum InvocationCacheStatus {
  inProgress = 'inProgress',
  baseline = 'baseline',
  warmingUp = 'warmingUp',
  healthy = 'healthy',
  lowReuse = 'lowReuse',
  noActivity = 'noActivity',
}

export type InvocationCacheStatusInfo = {
  status: InvocationCacheStatus;
  label: string;
  tooltip: string;
};

const BENCHMARK_PHASE_BASELINE = 'baseline';
const BENCHMARK_PHASE_WARMUP = 'warmup';

const BYTES_IN_MB = 1000 * 1000;

// The design keeps the amounts short: a single decimal at most, and none for whole numbers ("8.6 MB", "118 MB").
const MB_FORMATTER = new Intl.NumberFormat('en-US', { maximumFractionDigits: 1 });

const formatMB = (bytes: number) => `${MB_FORMATTER.format(bytes / BYTES_IN_MB)} MB`;

const transferTooltip = (downloadBytes: number, uploadBytes: number) =>
  `${formatMB(downloadBytes)} downloaded • ${formatMB(uploadBytes)} uploaded`;

export type CacheStatusSource = Pick<
  BuildToolInvocationWithDetails,
  'status' | 'benchmarkPhase' | 'cacheDownloadBytes' | 'cacheUploadBytes'
>;

/**
 * Describes how well the remote cache served a single invocation, based on how much it downloaded versus uploaded.
 */
export const getInvocationCacheStatus = ({
  status,
  benchmarkPhase,
  cacheDownloadBytes,
  cacheUploadBytes,
}: CacheStatusSource): InvocationCacheStatusInfo => {
  // The presenter writes both amounts together off one Insights `Stats`, so a half-present pair can only come from a
  // malformed payload — there is no measurement to read either way.
  const transferIsUnmeasured = cacheDownloadBytes == null || cacheUploadBytes == null;

  if (status === BuildToolInvocationStatus.pending) {
    return {
      status: InvocationCacheStatus.inProgress,
      label: 'In progress',
      tooltip: 'Build is still running. Cache status will update when it completes.',
    };
  }

  // Only the Gradle and Xcode analytics services parse a `benchmarkPhase` off the invocation payload — Bazel and
  // React Native invocations never carry one, so those two tools never reach Baseline or Warming up.
  if (benchmarkPhase === BENCHMARK_PHASE_BASELINE) {
    return {
      status: InvocationCacheStatus.baseline,
      label: 'Baseline',
      tooltip: 'Reference run without cache.',
    };
  }

  // Insights supplies these amounts on a tight, retry-free budget that the list tolerates failing, so an absent pair
  // means "not measured here" rather than "nothing moved". It shares the No activity presentation — the ticket asks
  // for one visual treatment — but not its tooltip, which would otherwise assert a cause for a build nobody measured.
  if (transferIsUnmeasured) {
    return {
      status: InvocationCacheStatus.noActivity,
      label: 'No activity',
      tooltip: 'No cache transfer was recorded for this invocation.',
    };
  }

  // Whatever the invocation's own outcome was, the cache did nothing for it.
  if (cacheDownloadBytes === 0 && cacheUploadBytes === 0) {
    return {
      status: InvocationCacheStatus.noActivity,
      label: 'No activity',
      tooltip: 'No cache data was transferred. The command may not be cacheable, or cache may be turned off.',
    };
  }

  if (benchmarkPhase === BENCHMARK_PHASE_WARMUP) {
    return {
      status: InvocationCacheStatus.warmingUp,
      label: 'Warming up',
      tooltip: transferTooltip(cacheDownloadBytes, cacheUploadBytes),
    };
  }

  // An invocation that downloaded exactly as much as it uploaded counts as reuse: the cache served everything it was
  // asked for, it just also stored the same amount back.
  if (cacheDownloadBytes >= cacheUploadBytes) {
    return {
      status: InvocationCacheStatus.healthy,
      label: 'Healthy',
      tooltip: transferTooltip(cacheDownloadBytes, cacheUploadBytes),
    };
  }

  return {
    status: InvocationCacheStatus.lowReuse,
    label: 'Low reuse',
    tooltip: transferTooltip(cacheDownloadBytes, cacheUploadBytes),
  };
};
